// three.js core + addons, self-hosted as a single minified bundle (assets/apc-three.min.js,
// mapped to 'apc-three' in snippets/scripts.liquid). Replaces 16 third-party esm.sh requests
// with one same-origin file — far less network waterfall and parse time on load.
import { THREE, EffectComposer, RenderPass, ShaderPass, VignetteShader, OutputPass, WebGL } from 'apc-three';

// Seeded PRNG (mulberry32) for deterministic, repeatable randomisation.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Evenly distributed points on a sphere via the golden-spiral method. `jitter`
// (unit-sphere space) nudges each point off the lattice by a random vector then
// renormalises it back onto the sphere — this keeps the even overall coverage
// (no bald patches or clumps) while breaking up the visible spiral rings and
// the lined-up look. 0 = perfectly regular, ~0.12 = a subtle hand-scattered
// nudge, ~0.5 = chaotic (overlaps + big gaps).
function fibonacciSpherePoints(count, radius = 1, jitter = 0, rng = Math.random) {
  const points = [];
  const golden = Math.PI * (3 - Math.sqrt(5));
  const denom = count > 1 ? count - 1 : 1;
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / denom) * 2;
    const ring = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = golden * i;
    let px = Math.cos(theta) * ring;
    let py = y;
    let pz = Math.sin(theta) * ring;
    if (jitter > 0) {
      px += (rng() * 2 - 1) * jitter;
      py += (rng() * 2 - 1) * jitter;
      pz += (rng() * 2 - 1) * jitter;
      const len = Math.hypot(px, py, pz) || 1;
      px /= len;
      py /= len;
      pz /= len;
    }
    points.push({ x: px * radius, y: py * radius, z: pz * radius });
  }
  return points;
}

// Min-separation relaxation: after jittering, some tiles inevitably drift close
// together. Each iteration pushes apart any pair whose centres are closer than
// `tileRadii[i] + tileRadii[j] + gap` — a SIZE-AWARE distance, so two big tiles are
// kept farther apart than two small ones and no pair ever sits edge-to-edge. It
// then reprojects every point onto the sphere AND clamps it to within `maxDrift`
// of its original even anchor. The repulsion stops images sitting next to each
// other; the anchor clamp stops any tile wandering far enough to tear open a big
// empty gap — so coverage stays even while the scattered look survives.
function relaxMinSeparation(points, anchors, radius, tileRadii, gap, maxDrift, iterations = 16) {
  const n = points.length;
  for (let iter = 0; iter < iterations; iter++) {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const minDist = tileRadii[i] + tileRadii[j] + gap;
        const a = points[i];
        const b = points[j];
        let dx = a.x - b.x;
        let dy = a.y - b.y;
        let dz = a.z - b.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < minDist * minDist && d2 > 1e-9) {
          const d = Math.sqrt(d2);
          const push = ((minDist - d) * 0.5) / d;
          dx *= push;
          dy *= push;
          dz *= push;
          a.x += dx;
          a.y += dy;
          a.z += dz;
          b.x -= dx;
          b.y -= dy;
          b.z -= dz;
        }
      }
    }
    for (let i = 0; i < n; i++) {
      const p = points[i];
      const anchor = anchors[i];
      // Pull back toward the even anchor if it drifted past maxDrift, then snap
      // the result onto the sphere surface.
      let dx = p.x - anchor.x;
      let dy = p.y - anchor.y;
      let dz = p.z - anchor.z;
      const drift = Math.hypot(dx, dy, dz);
      if (drift > maxDrift) {
        const t = maxDrift / drift;
        p.x = anchor.x + dx * t;
        p.y = anchor.y + dy * t;
        p.z = anchor.z + dz * t;
      }
      const s = radius / (Math.hypot(p.x, p.y, p.z) || 1);
      p.x *= s;
      p.y *= s;
      p.z *= s;
    }
  }
  return points;
}

const DEFAULTS = {
  tileCount: 44,
  sphereRadius: 3.2,
  // How far each tile is nudged off the even golden-spiral lattice (unit-sphere
  // space, ~radians of arc). Scattered freely now — the relaxation pass below
  // pulls apart anything that lands too close, so this can be generous without
  // clumping. 0 = regular spiral.
  jitter: 0.2,
  // Minimum empty gap (world-space chord) kept between the EDGES of any two tiles.
  // Relaxation enforces centre distance >= radiusA + radiusB + tileGap using each
  // tile's actual (random) size, so large and small tiles alike never sit
  // edge-to-edge. Raise for more breathing room, lower to let them sit closer.
  tileGap: 0.15,
  // Max distance (world-space chord) a tile may drift from its even golden-spiral
  // anchor. Caps the scatter so no tile abandons its neighbourhood and leaves a
  // big empty gap. Lower = tighter to the even layout, higher = more gaps allowed.
  maxDrift: 0.4,
  tileArea: 0.16,
  tileScale: 1.6,
  // Per-tile random size, as a multiplier on tileScale. Each tile picks a value
  // in [min, max] so the sphere mixes smaller and larger images instead of one
  // uniform size — −30%…+50%, so the largest tiles read clearly bigger than the
  // smallest (1.5 / 0.7 ≈ 2.1× span).
  tileSizeRange: { min: 0.7, max: 1.5 },
  camera: { fov: 45, distanceMobile: 16.5 },
  // desktopHeightFraction / mobileWidthFraction set the globe's apparent diameter
  // as a fraction of viewport height / width. desktopOffsetVw shifts the globe
  // right of centre (vw); desktopRaiseVh lifts it up from the bottom anchor (vh).
  framing: { desktopHeightFraction: 1.75, desktopOffsetVw: 5, desktopRaiseVh: 0, mobileWidthFraction: 0.945 },
  placement: { mobile: { x: 0, y: 0 } },
  autoRotateSpeed: 0.0013, // idle spin near the centre (rad/frame)
  autoRotateEdgeBoost: 0.5, // extra spin toward the canvas edges: ×(1 + this) at the far side — subtle
  // drift: tilt velocity (rad/frame) at full pointer offset — the sphere slowly keeps
  // rotating toward wherever the cursor rests, instead of leaning to a fixed target.
  autoTilt: { amplitude: 0.24, speed: 0.004, ease: 0.03, drift: 0.0008 },
  dragSensitivity: 0.005,
  maxDragSpeed: 0.02, // rad per pointer move — caps fast flicks so the sphere can't lurch/teleport
  inertiaDamping: 0.94,
  maxTiltX: 0.6,
  // Zoom glides with inertia: each wheel notch / pinch seeds a velocity that the loop
  // integrates and damps, so the zoom keeps drifting after you stop and eases to rest.
  zoom: { min: 0.8, max: 1.6, wheelStep: 0.00006, damping: 0.91 },
  // Dreamy motion trails via frame feedback (a full-screen post pass, not per-card):
  // each frame composites the freshly rendered sphere over a faded copy of the previous
  // frame, sampled a step back along the sphere's CURRENT on-screen motion. That gives
  // real temporal motion blur for free, and the trail streaks the way the sphere is
  // actually travelling (spin/drag/inertia) instead of always pulling to one corner. The
  // drift direction is recomputed every frame from the rotation delta. See FeedbackPass.
  feedback: {
    fade: 0.925, // trail length: fraction of the trail buffer that survives each frame (lower = shorter)
    shift: 0.003, // screen-fraction the trail drifts per frame — the smear speed/length
    strength: 0.5, // trail opacity (display only, so it can't compound)
    blur: 0.0016, // per-frame blur radius (screen-fraction); compounds as the trail fades, so older
    // smear softens into a dreamy blur instead of staying pixel-sharp
    dirX: -1, // seed drift direction (frame 1 only) and reference drift LENGTH — the live
    dirY: -0.5, // direction then tracks the sphere's motion; |dir| keeps the tuned smear length
    // Wind: the WHOLE trail leans together — one shared sideways sway (perpendicular to the
    // drift), applied display-only so it's bounded to ±amplitude and never accumulates.
    // amplitude is the max sway in screen-fraction (~0.0015 ≈ 1–2px); speed is rad/frame.
    wind: { amplitude: 0.004, speed: 0.02 },
  },
  // The design's middle layer: behind each sharp card sits a blurred copy of the
  // same image, peeking out as a thin soft fringe — a few px past the card's edges
  // (sharp image / blurred copy / motion trail, bottom to top). scale is relative
  // to the card, bias is the mip LOD used for the blur, edge is the UV fraction
  // over which the fringe's borders dissolve.
  // The fringe is a fade-out toward the border. edge (in quad-UV) is wide so the
  // outer halo dissolves softly — this is the dreamy glow past each card's edges.
  // A separate full-coverage backing layer (below) keeps the card itself opaque, so
  // the wide soft fringe here no longer means you can see through the card.
  // additive: blend like light (brighter-of), so the halo glows + blends into the
  // motion trail instead of reading as a faded photo.
  blurLayer: { scale: 1.3, bias: 3.0, opacity: 1.0, edge: 0.3, additive: true },
  // Backing layer: a blurred copy at (just over) the card's own size, full opacity and
  // a near-hard edge, sitting right behind the semi-transparent sharp photo. It fills
  // the whole card footprint with blur so you read the dreamy ghost THROUGH the sharp
  // card — never the background — while the soft `blurLayer` fringe still glows outside.
  backingLayer: { scale: 1.04, bias: 3.0, opacity: 1.0, edge: 0.04 },
  fogColor: 0x050505,
  fogDensity: 0.05,
  vignette: { offset: 1.05, darkness: 1.15 },
};

function readConfig(root) {
  const scope = root.closest('.apc-sphere-gallery') ?? root;
  const el = scope.querySelector('[data-apc-sphere-config]');
  let raw = {};
  try {
    raw = JSON.parse(el?.textContent || '{}');
  } catch (e) {
    console.warn('[apc-sphere-gallery] bad config JSON', e);
  }
  const blockTiles = (raw.tiles || []).filter((t) => t && t.src);
  const tiles = blockTiles.length ? blockTiles : raw.fallbackTiles || [];

  const cfg = structuredClone(DEFAULTS);
  cfg.images = tiles.map((t) => ({ src: t.src, aspect: Number(t.aspect) || 1 }));
  if (Number.isFinite(raw.tileCount)) cfg.tileCount = raw.tileCount;
  if (Number.isFinite(raw.jitter)) cfg.jitter = raw.jitter;
  if (Number.isFinite(raw.tileGap)) cfg.tileGap = raw.tileGap;
  if (Number.isFinite(raw.maxDrift)) cfg.maxDrift = raw.maxDrift;
  if (Number.isFinite(raw.heightFraction)) cfg.framing.desktopHeightFraction = raw.heightFraction;
  if (Number.isFinite(raw.offsetVw)) cfg.framing.desktopOffsetVw = raw.offsetVw;
  return cfg;
}

function initGallery(root) {
  const CONFIG = readConfig(root);
  if (!CONFIG.images.length) {
    console.warn('[apc-sphere-gallery] no tile images — skipping init');
    return;
  }

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(root.clientWidth, root.clientHeight);
  renderer.setClearColor(0x000000, 0);
  root.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(CONFIG.fogColor, CONFIG.fogDensity);

  const camera = new THREE.PerspectiveCamera(CONFIG.camera.fov, root.clientWidth / root.clientHeight, 0.1, 100);
  camera.position.z = CONFIG.camera.distanceMobile;

  const group = new THREE.Group();
  scene.add(group);

  // Hold the globe hidden until every tile texture has decoded, then reveal it all at
  // once — otherwise on a slow (mobile) connection the sphere shows first and the
  // photos pop in one by one. The LoadingManager fires onLoad once every texture has
  // ended (including any that error), and a timeout backstops it so the globe can
  // never stay stuck hidden if a single load hangs.
  group.visible = false;
  const manager = new THREE.LoadingManager();
  const showGlobe = () => {
    if (group.visible) return;
    clearTimeout(showGlobeTimer);
    group.visible = true;
    root.dataset.apcTexturesReady = '1';
    root.dispatchEvent(new CustomEvent('apc-sphere:textures-ready', { bubbles: true }));
  };
  manager.onLoad = showGlobe;
  const showGlobeTimer = setTimeout(showGlobe, 6000);

  const loader = new THREE.TextureLoader(manager);
  const tileDefs = CONFIG.images.map(({ src, aspect }) => {
    const texture = loader.load(src);
    texture.colorSpace = THREE.SRGBColorSpace;
    const h = Math.sqrt(CONFIG.tileArea / aspect);
    const geometry = new THREE.PlaneGeometry(h * aspect, h);
    return { texture, geometry };
  });

  // Deterministic PRNG so placement is randomised but stable across renders.
  const rng = mulberry32(0x5f3a7c1);
  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Spread each image as evenly as possible across the tiles, then shuffle so
  // identical images land in random, non-sequential positions (no spiral seam).
  // Even distribution caps repeats at ceil(tileCount / imageCount) per image.
  const tileAssignments = [];
  for (let i = 0; i < CONFIG.tileCount; i++) tileAssignments.push(i % tileDefs.length);
  shuffle(tileAssignments);

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Shared vertex shader for the card's extra layers (currently just the halo): a plain
  // model-view-projection pass-through that also feeds the fog varyings.
  const cardLayerVertexShader = /* glsl */ `
    varying vec2 vUv;
    #include <fog_pars_vertex>
    void main() {
      vUv = uv;
      vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
      gl_Position = projectionMatrix * mvPosition;
      #include <fog_vertex>
    }
  `;

  // Blur-layer quad: the design's middle layer — a blown-up, defocused copy of the
  // card hovering just behind it, so each sharp photo sits in its own soft halo.
  // The blur is a deep mip pick (uBias) and the borders dissolve over uEdge of the
  // quad, like a Figma layer-blur bleeding past the card's bounds.
  const haloFragmentShader = /* glsl */ `
    uniform sampler2D map;
    uniform float uBias;
    uniform float uOpacity;
    uniform float uEdge;
    varying vec2 vUv;
    #include <fog_pars_fragment>
    void main() {
      vec3 col = texture2D( map, vUv, uBias ).rgb;
      float edge = smoothstep( 0.0, uEdge, vUv.x ) * smoothstep( 1.0, 1.0 - uEdge, vUv.x )
                 * smoothstep( 0.0, uEdge, vUv.y ) * smoothstep( 1.0, 1.0 - uEdge, vUv.y );
      gl_FragColor = vec4( col, uOpacity * edge );
      #include <fog_fragment>
    }
  `;

  // Pre-pick each tile's random size, and from it a collision radius (half-diagonal
  // of the scaled plane), so the relaxation can keep bigger tiles farther apart than
  // small ones. Plane is sqrt(tileArea·aspect) wide × sqrt(tileArea/aspect) tall.
  const sizeRange = CONFIG.tileSizeRange;
  const tileScales = [];
  const tileRadii = [];
  for (let i = 0; i < CONFIG.tileCount; i++) {
    const scale = CONFIG.tileScale * (sizeRange.min + rng() * (sizeRange.max - sizeRange.min));
    const aspect = CONFIG.images[tileAssignments[i]].aspect || 1;
    const w = Math.sqrt(CONFIG.tileArea * aspect) * scale;
    const h = Math.sqrt(CONFIG.tileArea / aspect) * scale;
    tileScales.push(scale);
    tileRadii.push(0.5 * Math.hypot(w, h));
  }

  // Even anchors (no jitter) — the relaxation clamps each tile to stay near these
  // so the scatter never opens a big gap.
  const anchors = fibonacciSpherePoints(CONFIG.tileCount, CONFIG.sphereRadius);
  const tilePoints = fibonacciSpherePoints(CONFIG.tileCount, CONFIG.sphereRadius, CONFIG.jitter, rng);
  relaxMinSeparation(tilePoints, anchors, CONFIG.sphereRadius, tileRadii, CONFIG.tileGap, CONFIG.maxDrift);

  // Cards we raycast against for click/tap detection.
  const tiles = [];

  // A blurred copy of a card, built from a layer config — shared by the soft outer
  // halo and the opaque backing fill (they differ only in scale / edge / opacity).
  const makeBlurLayer = (def, layerCfg, z) => {
    const uniforms = THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      {
        uBias: { value: layerCfg.bias },
        uOpacity: { value: layerCfg.opacity },
        uEdge: { value: layerCfg.edge },
      },
    ]);
    uniforms.map = { value: def.texture };
    const mesh = new THREE.Mesh(
      def.geometry,
      new THREE.ShaderMaterial({
        uniforms,
        vertexShader: cardLayerVertexShader,
        fragmentShader: haloFragmentShader,
        transparent: true,
        depthWrite: false,
        blending: layerCfg.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
        side: THREE.DoubleSide,
        fog: true,
      }),
    );
    mesh.scale.set(layerCfg.scale, layerCfg.scale, 1);
    mesh.position.z = z;
    return mesh;
  };

  tilePoints.forEach((p, i) => {
    const def = tileDefs[tileAssignments[i]];
    const material = new THREE.MeshBasicMaterial({ map: def.texture, side: THREE.DoubleSide, fog: true, transparent: true, opacity: 0.6 });
    const tile = new THREE.Mesh(def.geometry, material);
    const scale = tileScales[i];
    tile.scale.set(scale, scale, 1);
    tile.position.set(p.x, p.y, p.z);
    tile.lookAt(p.x * 2, p.y * 2, p.z * 2);
    tiles.push(tile);
    group.add(tile);

    // Backing: a full-coverage blurred fill sitting just behind the semi-transparent
    // sharp photo, so the dreamy ghost reads THROUGH the card — never the background.
    tile.add(makeBlurLayer(def, CONFIG.backingLayer, -0.01));
    // Outer halo: a larger, soft-edged blurred copy that dissolves past the card's
    // edges — the dreamy glow. Farther back than the backing so it sorts behind it.
    tile.add(makeBlurLayer(def, CONFIG.blurLayer, -0.02));
  });

  // ── Frame-feedback trail pass ──────────────────────────────────────────────────────
  // A full-screen post pass with a pair of ping-pong targets holding a light-trail buffer.
  // Each frame the buffer becomes max(scene, previous · fade), with the previous sampled a
  // step against the live drift direction so its content streaks along the sphere's current
  // on-screen motion, blurred a touch each frame so it stays soft. The crisp scene is then composited
  // ON TOP of a dimmed copy, so the trail sits BEHIND the images and the photos stay sharp.
  // `fade` sets length, `strength` visibility (display-only, so it never compounds), `blur` softness.
  //
  // We don't depend on three's FullScreenQuad/Pass helpers (not guaranteed in the bundle):
  // the pass is a duck-typed object that EffectComposer drives like any other pass, and we
  // draw full-screen with our own clip-space quad + ortho camera.
  const fsCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const fsScene = new THREE.Scene();
  const fsQuadGeo = new THREE.PlaneGeometry(2, 2);
  const fsQuad = new THREE.Mesh(fsQuadGeo, null);
  fsQuad.frustumCulled = false;
  fsScene.add(fsQuad);
  const blitTo = (material, target) => {
    fsQuad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(fsScene, fsCamera);
  };

  const fsVertexShader = /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4( position.xy, 0.0, 1.0 );
    }
  `;
  // Reference drift length (the tuned smear), and the live drift direction in screen space
  // (+x right / +y up). fbDir is eased toward the sphere's on-screen motion each frame and
  // renormalised to fbDriftLen, so the streak follows the spin but keeps a constant length.
  const fbDriftLen = Math.hypot(CONFIG.feedback.dirX, CONFIG.feedback.dirY) || 1;
  const fbDir = new THREE.Vector2(CONFIG.feedback.dirX, CONFIG.feedback.dirY);
  // Unit perpendicular to the drift direction — the wind sways the whole trail along this
  // axis (see the composite pass). Refreshed each frame from fbDir.
  const fbPerp = new THREE.Vector2();

  // Pass 1 — grow the light-trail buffer: max(scene, previous · fade), with the previous
  // sampled a step against `dir` so the trail drifts steadily toward one screen edge.
  const trailMaterial = new THREE.ShaderMaterial({
    uniforms: {
      tScene: { value: null }, // freshly rendered sphere this frame
      tPrev: { value: null }, // previous trail buffer
      uFade: { value: CONFIG.feedback.fade },
      uOffset: {
        value: new THREE.Vector2(
          -CONFIG.feedback.dirX * CONFIG.feedback.shift,
          -CONFIG.feedback.dirY * CONFIG.feedback.shift,
        ),
      },
      uBlur: { value: CONFIG.feedback.blur },
    },
    depthTest: false,
    depthWrite: false,
    vertexShader: fsVertexShader,
    fragmentShader: /* glsl */ `
      uniform sampler2D tScene;
      uniform sampler2D tPrev;
      uniform float uFade;
      uniform vec2 uOffset;
      uniform float uBlur;
      varying vec2 vUv;
      void main() {
        // Read the previous buffer one step back along the streak (so the trail drifts that
        // way each frame) and box-blur the sample — the blur compounds as the trail fades, so
        // older smear softens into a dreamy blur instead of staying pixel-sharp.
        vec2 prevUv = vUv + uOffset;
        vec4 prev = vec4( 0.0 );
        for ( int x = -1; x <= 1; x++ ) {
          for ( int y = -1; y <= 1; y++ ) {
            prev += texture2D( tPrev, prevUv + vec2( float( x ), float( y ) ) * uBlur );
          }
        }
        prev *= uFade / 9.0;
        vec2 inb = step( vec2( 0.0 ), prevUv ) * step( prevUv, vec2( 1.0 ) );
        prev *= inb.x * inb.y;
        vec4 cur = texture2D( tScene, vUv );
        gl_FragColor = max( cur, prev ); // brighter-of → clean light trails on the dark sphere
      }
    `,
  });
  // Pass 2 — composite the crisp scene OVER a dimmed copy of the trail buffer, so the trail
  // sits BEHIND the images (photos stay sharp); uStrength tunes how visible the trail is.
  const compositeMaterial = new THREE.ShaderMaterial({
    uniforms: {
      tScene: { value: null },
      tTrail: { value: null },
      uStrength: { value: CONFIG.feedback.strength },
      uWind: { value: new THREE.Vector2(0, 0) }, // shared sway offset, refreshed each frame
    },
    depthTest: false,
    depthWrite: false,
    vertexShader: fsVertexShader,
    fragmentShader: /* glsl */ `
      uniform sampler2D tScene;
      uniform sampler2D tTrail;
      uniform float uStrength;
      uniform vec2 uWind;
      varying vec2 vUv;
      void main() {
        vec4 cur = texture2D( tScene, vUv );
        // Wind: read the trail at one shared, slowly swaying offset — the whole trail leans
        // together (display-only, so it's bounded and never feeds back into the buffer).
        vec4 trail = texture2D( tTrail, vUv + uWind ) * uStrength;
        gl_FragColor = cur + trail * ( 1.0 - cur.a ); // scene on top, trail shows only behind it
      }
    `,
  });

  const makeAccumTarget = () =>
    new THREE.WebGLRenderTarget(1, 1, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      type: THREE.HalfFloatType, // smooth long fades without 8-bit banding
      depthBuffer: false,
      stencilBuffer: false,
    });
  const accum = [makeAccumTarget(), makeAccumTarget()];
  let accumIdx = 0; // accum[accumIdx] holds the latest trail buffer

  const feedbackPass = {
    enabled: !reduceMotion, // reduced motion → no trails, just scene + vignette
    needsSwap: true,
    clear: false,
    renderToScreen: false,
    setSize(w, h) {
      accum[0].setSize(w, h);
      accum[1].setSize(w, h);
      // Clear both so frame 1 trails from black instead of GPU garbage.
      renderer.setRenderTarget(accum[0]);
      renderer.clear();
      renderer.setRenderTarget(accum[1]);
      renderer.clear();
    },
    render(_renderer, writeBuffer, readBuffer) {
      const prev = accum[accumIdx];
      const next = accum[accumIdx ^ 1];
      trailMaterial.uniforms.tScene.value = readBuffer.texture;
      trailMaterial.uniforms.tPrev.value = prev.texture;
      blitTo(trailMaterial, next); // grow the trail buffer → next
      compositeMaterial.uniforms.tScene.value = readBuffer.texture;
      compositeMaterial.uniforms.tTrail.value = next.texture;
      blitTo(compositeMaterial, this.renderToScreen ? null : writeBuffer); // scene over dimmed trail
      accumIdx ^= 1; // next becomes prev for the following frame
    },
  };

  const composer = new EffectComposer(renderer);
  composer.setSize(root.clientWidth, root.clientHeight);
  composer.addPass(new RenderPass(scene, camera));
  composer.addPass(feedbackPass);

  const vignette = new ShaderPass(VignetteShader);
  vignette.uniforms.offset.value = CONFIG.vignette.offset;
  vignette.uniforms.darkness.value = CONFIG.vignette.darkness;
  composer.addPass(vignette);

  composer.addPass(new OutputPass());

  const baseSpin = reduceMotion ? 0 : CONFIG.autoRotateSpeed;

  // Drag-to-rotate and zoom (wheel/pinch) are intentionally disabled — too much
  // direct manipulation made the globe read as glitchy. The sphere is steered only
  // by where the cursor rests (idle spin + tilt), and a tap on a card opens the
  // teaser. velX/velY remain as the auto-spin/tilt inertia state.
  let velX = 0;
  let velY = baseSpin;
  let tiltPhase = 0;
  // Idle spin follows the pointer's horizontal offset from centre, −1 (left edge) …
  // +1 (right edge): the SIGN sets the direction (left half spins one way, right the
  // other) and the MAGNITUDE ramps the speed — slow near the centre, faster toward the
  // edges. Drag inertia overrides it until it decays.
  let spinX = 1;
  // Pointer's vertical offset from centre, −1 (top) … +1 (bottom) — drives the tilt drift.
  let pointerTilt = 0;
  let tiltDrift = 0; // eased tilt velocity so direction changes glide instead of stepping

  const canvas = renderer.domElement;

  // Hover/click on the cards: raycast the pointer against the tiles. A press that
  // barely moves counts as a click (and opens the teaser); anything more is a drag.
  const raycaster = new THREE.Raycaster();
  const pointerNdc = new THREE.Vector2();
  let hoveredTile = null;
  let downX = 0;
  let downY = 0;
  let downId = null;
  const CLICK_SLOP = 6; // px of pointer travel under which a press is a click, not a drag

  const tileAt = (clientX, clientY) => {
    const rect = canvas.getBoundingClientRect();
    pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointerNdc, camera);
    return raycaster.intersectObjects(tiles, false)[0]?.object ?? null;
  };

  canvas.addEventListener('pointerdown', (e) => {
    // Record the press purely so a barely-moved release can be read as a tap on a
    // card (below). No drag-to-rotate, no pinch — those are disabled by design.
    canvas.setPointerCapture(e.pointerId);
    downX = e.clientX;
    downY = e.clientY;
    downId = e.pointerId;
  });

  canvas.addEventListener('pointermove', (e) => {
    // Pointer's horizontal offset from centre, −1 … +1 — sets the idle-spin direction
    // (sign) and how fast it spins (magnitude: slow at centre, faster toward the edges).
    const rect = canvas.getBoundingClientRect();
    const half = rect.width / 2;
    spinX = THREE.MathUtils.clamp((e.clientX - (rect.left + half)) / half, -1, 1);
    // Vertical offset from centre drives the idle tilt target (top → up, bottom → down).
    pointerTilt = THREE.MathUtils.clamp((e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2), -1, 1);

    // Highlight the card under the cursor and flag it as clickable. (Touch never
    // hovers, so the pointer cursor only ever shows on a mouse.)
    hoveredTile = tileAt(e.clientX, e.clientY);
    canvas.classList.toggle('is-over-tile', !!hoveredTile);
  });

  function endPointer(e) {
    // A press that barely moved is a click/tap: if it landed on a card, open the
    // teaser. The liquid overlay script listens for this and runs its open().
    if (e.pointerId === downId) {
      const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
      if (moved < CLICK_SLOP && tileAt(e.clientX, e.clientY)) {
        root.dispatchEvent(new CustomEvent('apc-sphere:tile-activate', { bubbles: true }));
      }
      downId = null;
    }
    if (e.pointerId !== undefined && canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
  }
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);

  function updateRotation() {
    // Direction from the sign, speed ramped by how far the pointer sits from centre.
    const spinDir = spinX < 0 ? -1 : 1;
    const targetSpin = baseSpin * spinDir * (1 + CONFIG.autoRotateEdgeBoost * Math.abs(spinX));
    velY = velY * CONFIG.inertiaDamping + targetSpin * (1 - CONFIG.inertiaDamping);
    group.rotation.y += velY;
    velX *= CONFIG.inertiaDamping;
    tiltPhase += CONFIG.autoTilt.speed;
    // The pointer's vertical offset drives a tiny continuous tilt VELOCITY, not a
    // position target: sweeping the cursor top-to-bottom barely moves the sphere a few
    // pixels, but a cursor parked high or low keeps it drifting that way until the tilt
    // limit. A gentle ambient wobble rides on top so it stays alive when the cursor is
    // still. (Horizontal works the same way via the idle spin's pointer-side direction.)
    const wobbleVel = reduceMotion ? 0 : CONFIG.autoTilt.amplitude * 0.35 * Math.cos(tiltPhase) * CONFIG.autoTilt.speed;
    const driftTarget = reduceMotion ? 0 : pointerTilt * CONFIG.autoTilt.drift;
    tiltDrift += (driftTarget - tiltDrift) * CONFIG.autoTilt.ease;
    group.rotation.x = THREE.MathUtils.clamp(
      group.rotation.x + velX + tiltDrift + wobbleVel,
      -CONFIG.maxTiltX,
      CONFIG.maxTiltX,
    );
  }

  function resize() {
    const w = root.clientWidth;
    const h = root.clientHeight;
    if (!w || !h) return;
    const aspect = w / h;
    const portrait = h >= w;
    const R = CONFIG.sphereRadius;
    const tan = Math.tan(THREE.MathUtils.degToRad(CONFIG.camera.fov) / 2);
    camera.aspect = aspect;

    if (portrait) {
      // Mobile: distance so the sphere's apparent diameter is a fraction of the
      // viewport WIDTH, so the globe never bleeds off the sides on narrow screens
      // (same perspective math as desktop, framed by width instead of height).
      const f = CONFIG.framing;
      const denom = tan * aspect * f.mobileWidthFraction;
      const distance = R * Math.sqrt(1 + 1 / (denom * denom));
      camera.position.z = distance;
      camera.updateProjectionMatrix();
      const halfH = distance * tan;
      const halfW = halfH * aspect;
      const p = CONFIG.placement.mobile;
      group.position.set(p.x * 2 * halfW, p.y * 2 * halfH, 0);
    } else {
      // Desktop: distance so the sphere's apparent diameter is a fixed fraction of
      // viewport height (aspect-independent); apparentR accounts for perspective enlargement.
      const f = CONFIG.framing;
      const denom = tan * f.desktopHeightFraction;
      const distance = R * Math.sqrt(1 + 1 / (denom * denom));
      camera.position.z = distance;
      camera.updateProjectionMatrix();
      const halfH = distance * tan;
      const halfW = halfH * aspect;
      const apparentR = R * (distance / Math.sqrt(distance * distance - R * R));
      // Horizontally centred, then shifted desktopOffsetVw to the right. Vertically
      // anchored on the floor (-halfH + apparentR), then lifted desktopRaiseVh up.
      // Full viewport is 2·halfW wide / 2·halfH tall = 100vw / 100vh, so 1vw =
      // halfW/50 and 1vh = halfH/50.
      const cx = (f.desktopOffsetVw / 50) * halfW;
      const cy = -halfH + apparentR + (f.desktopRaiseVh / 50) * halfH;
      group.position.set(cx, cy, 0);
    }

    renderer.setSize(w, h);
    composer.setSize(w, h);
  }

  const ro = new ResizeObserver(resize);
  ro.observe(root);
  resize();

  // Pause the render loop when offscreen or the tab is hidden.
  let paused = false;
  let onScreen = false;
  const setPaused = () => {
    paused = !onScreen || document.hidden;
  };
  const io = new IntersectionObserver(
    ([entry]) => {
      onScreen = entry.isIntersecting;
      setPaused();
    },
    { threshold: 0.01 },
  );
  io.observe(root);
  document.addEventListener('visibilitychange', setPaused);

  let trailTime = 0;
  let rafId = 0;
  // Previous-frame rotation, so we can read how far (and which way) the sphere turned.
  let prevRotX = group.rotation.x;
  let prevRotY = group.rotation.y;
  const fbDirTarget = new THREE.Vector2();
  const FB_DIR_EASE = 0.06; // how fast the drift swings to a new heading (reversals glide, not snap)
  function animate(now = performance.now()) {
    rafId = requestAnimationFrame(animate);
    if (paused) return;
    updateRotation();

    // Steer the trail OPPOSITE the sphere's on-screen motion, so the streak lags behind the
    // cards like a real motion trail. Spinning around Y moves cards horizontally, around X
    // vertically (a +x turn pushes them DOWN), so the on-screen travel is (Δrot.y, −Δrot.x)
    // and the trail drifts the other way: (−Δrot.y, Δrot.x). Ease toward it and renormalise
    // to the tuned drift length, so the streak keeps a constant length and glides through
    // reversals. When motion ≈ 0 (paused mid-drag) we keep the last heading.
    const dRotY = group.rotation.y - prevRotY;
    const dRotX = group.rotation.x - prevRotX;
    prevRotY = group.rotation.y;
    prevRotX = group.rotation.x;
    fbDirTarget.set(-dRotY, dRotX);
    if (fbDirTarget.lengthSq() > 1e-12) {
      fbDirTarget.setLength(fbDriftLen);
      fbDir.lerp(fbDirTarget, FB_DIR_EASE);
    }
    trailMaterial.uniforms.uOffset.value.set(-fbDir.x * CONFIG.feedback.shift, -fbDir.y * CONFIG.feedback.shift);
    // Wind sways perpendicular to the live drift direction.
    const perpLen = Math.hypot(fbDir.x, fbDir.y) || 1;
    fbPerp.set(-fbDir.y / perpLen, fbDir.x / perpLen);

    trailTime += CONFIG.feedback.wind.speed;
    const sway = Math.sin(trailTime) * CONFIG.feedback.wind.amplitude;
    compositeMaterial.uniforms.uWind.value.set(fbPerp.x * sway, fbPerp.y * sway);
    composer.render();
  }
  animate();

  // Cleanup hook so the Shopify theme editor can tear an instance down.
  root._apcSphereDispose = () => {
    cancelAnimationFrame(rafId);
    clearTimeout(showGlobeTimer);
    ro.disconnect();
    io.disconnect();
    tileDefs.forEach((d) => {
      d.texture.dispose();
      d.geometry.dispose();
    });
    trailMaterial.dispose();
    compositeMaterial.dispose();
    fsQuadGeo.dispose();
    accum.forEach((t) => t.dispose());
    composer.dispose?.();
    renderer.dispose();
    renderer.domElement.remove();
  };
}

function mount(root) {
  if (!root || root.dataset.apcSphereReady === 'true') return;
  root.dataset.apcSphereReady = 'true';
  if (WebGL.isWebGL2Available()) {
    initGallery(root);
  } else {
    root.classList.add('no-webgl'); // CSS hides canvas, shows poster
    console.warn('[apc-sphere-gallery] WebGL2 unavailable — showing poster fallback');
  }
}

function mountAll(scope = document) {
  scope.querySelectorAll('[data-apc-sphere]').forEach(mount);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll());
} else {
  mountAll();
}

// Shopify theme editor: (re)mount and dispose as sections are added/removed.
document.addEventListener('shopify:section:load', (e) => mountAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-apc-sphere]').forEach((root) => root._apcSphereDispose?.());
});
